import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Formik, Field } from 'formik';
import { containerSx } from './Wizard.styled';
import { sources } from 'constants/invoices';
import { useDispatch, useSelector } from 'react-redux';
import { InvoiceActions } from 'state/modules/invoice';
import { PartnersSelect, VehiclesSelect, VehiclesGroupsSelect } from 'components/AsyncFields';
import { InvoiceSourceTypesSelect } from 'components/DataFields';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { DateTimePickerField } from 'components/FormFields';
import { UserContext } from 'context';
import moment from 'moment';

export const BasicInfo = ({ formRef, errors, mode, action, handleSuccess }) => {
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const user = useSelector((state) => state.auth.user);
  const current = useSelector((state) => state.invoice.invoice);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const { localeFormatLower } = useSelector((state) => state.organizations);
  const organization_type = sharingType === 'FFB' ? 'BUSINESS' : 'SERVICE';

  const formSchema = Yup.object().shape({
    customer_id: Yup.string().required(i18n.t('common.required_field')),
    source_type: Yup.string().required(i18n.t('common.required_field')),
    source_id: Yup.string().required(i18n.t('common.required_field')),
    ...(sharingType === 'FAAS'
      ? {
          due_time: Yup.string().required(i18n.t('common.required_field')),
        }
      : {}),
  });

  const initialValues = {
    customer_id: current?.account_customer?.holder_id || '',
    source_type: current?.source_name
      ? sources.find((e) => e.source === current?.source_name)?.value
      : '',
    source_id: current?.source_id || '',
    due_time: current?.due_time || '',
  };

  const onSubmit = (values, type) => {
    const payload = {
      ...values,
      is_draft: true,
      emitter_type: 'organization',
      customer_type: 'organization',
      emitter_id: user?.organization?.id,
    };

    // remove empty fields
    Object.keys(payload).forEach((key) => !payload[key] && delete payload[key]);

    if (type === 'draft') {
      dispatch(InvoiceActions.fetchCreateInvoice(payload, true));
    }

    if (current?.id && action !== 'clone') {
      dispatch(InvoiceActions.fetchUpdateInvoice(current?.id, payload, mode, handleSuccess));
    } else {
      if (action === 'clone') {
        payload.items = current?.invoice_items?.map((item) => ({
          invoice_item_type_id: item?.invoice_item_type?.id,
          description: item?.description,
          unit_price: item?.unit_price,
          quantity: item?.quantity,
        }));
      }
      dispatch(InvoiceActions.fetchCreateInvoice(payload));
    }
  };

  const showCustomerInput = mode !== 'modal';
  const dueTimeIsOptional = sharingType === 'FFB';
  const inputColumns = 3;

  return (
    <Box sx={containerSx}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={11}>
            <Formik
              innerRef={formRef}
              onSubmit={onSubmit}
              enableReinitialize={true}
              initialValues={initialValues}
              validationSchema={formSchema}
            >
              {({ values, handleSubmit, setFieldValue }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={4}
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                  >
                    {showCustomerInput ? (
                      <Grid size={{ xs: 12, sm: 6, md: inputColumns }}>
                        <Typography variant="h6">
                          <Box fontWeight="500" fontSize={14}>
                            {i18n.t('common.To')}:
                          </Box>
                        </Typography>
                        <Field
                          margin="none"
                          name="customer_id"
                          component={PartnersSelect}
                          label={i18n.t('common.partner_name')}
                          asyncErrors={errors?.customer_id}
                          customParams={{
                            status: ['ACTIVE', 'CANREQUESTED'],
                            organization_type: [organization_type],
                          }}
                        />
                      </Grid>
                    ) : null}
                    <Grid size={{ xs: 12, sm: 6, md: inputColumns }}>
                      <Typography variant="h6">
                        <Box fontWeight="500" fontSize={14}>
                          {i18n.t('common.category')}:
                        </Box>
                      </Typography>
                      <Field
                        type="text"
                        margin="none"
                        name="source_type"
                        label={i18n.t('common.type')}
                        asyncErrors={errors?.source_id}
                        component={InvoiceSourceTypesSelect}
                        customParams={{
                          cleanUp: () => {
                            setFieldValue('source_id', '');
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: inputColumns }}>
                      <Typography variant="h6">
                        <Box fontWeight="500" fontSize={14}>
                          {i18n.t('common.related_to')}:
                        </Box>
                      </Typography>
                      <Field
                        margin="none"
                        name="source_id"
                        component={
                          values?.source_type === 'vehicle' ? VehiclesSelect : VehiclesGroupsSelect
                        }
                        asyncErrors={errors?.source_id}
                        label={
                          values?.source_type === 'vehicle'
                            ? i18n.t('common.vehicle')
                            : i18n.t('common.fleet')
                        }
                        disabled={values?.source_type === '' || values?.customer_id === ''}
                        customParams={{
                          status_filter: 'only',
                          statuses: 'ACTIVE',
                          ...(values?.source_type === 'vehicle'
                            ? {
                                group_type: orgType === 'msOwnerOrg' ? 'OWNER' : 'CUSTOMER',
                                service_id: values?.customer_id,
                                include_unshared: true,
                              }
                            : {
                                partner_id: values?.customer_id,
                              }),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: inputColumns }}>
                      <Typography variant="h6">
                        <Box fontWeight="500" fontSize={14}>
                          {i18n.t('common.when')}:
                        </Box>
                      </Typography>
                      <Field
                        margin="none"
                        editable={true}
                        name="due_time"
                        popperPlacement="bottom-end"
                        component={DateTimePickerField}
                        label={i18n.t('invoice.due_date')}
                        inputProps={{
                          autocomplete: 'off',
                        }}
                        showMonthYearPicker
                        {...(dueTimeIsOptional
                          ? {
                              isClearable: true,
                              dateFormat: localeFormatLower,
                              utc: true,
                              isStateless: true,
                              showTimeSelect: false,
                              form: {
                                setFieldValue: (_, val) => {
                                  setFieldValue(
                                    'due_time',
                                    moment(val).utc().startOf('day').toISOString()
                                  );
                                },
                              },
                            }
                          : {})}
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

BasicInfo.propTypes = {
  formRef: PropTypes.any,
  errors: PropTypes.object,
  mode: PropTypes.string,
  action: PropTypes.string,
  handleSuccess: PropTypes.func,
};
